import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { apiGetCards } from '../../../api/cardApi';
import { apiGetMyOwnedCards } from '../../../api/userCardApi';
import type { Card, CardRarity } from '../../../types/models/card';
import { useAppSelector } from '../../../store/hooks';
import CardDetailModal from '../../Cards/CardDetailModal';

const storageUrl = process.env.REACT_APP_STORAGE_URL ?? '';

const toImgSrc = (path: string | null | undefined) =>
  path ? (path.startsWith('http') ? path : storageUrl + path) : null;

const RARITY_COLOR: Record<CardRarity, string> = {
  common: 'rgba(255,255,255,0.55)',
  rare: '#4a9eff',
  epic: '#c27ff5',
  legendary: 'gold',
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 20px 14px 70px;
  min-height: calc(100vh - 70px);
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: gold;
  margin: 0 0 16px;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  &:focus { outline: none; border-color: gold; }
  &::placeholder { color: rgba(255,255,255,0.3); }
`;

const ExpansionSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  &:focus { outline: none; border-color: gold; }
  option { background-color: #1a1a1a; }
`;

const ToggleOwnedBtn = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.15)')};
  background-color: ${({ $active }) => ($active ? 'rgba(255,215,0,0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'gold' : 'rgba(255,255,255,0.5)')};
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
`;

const CountText = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  margin: 0 0 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const CardItem = styled.div<{ $owned: boolean }>`
  cursor: pointer;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background-color: #222;
  filter: ${({ $owned }) => ($owned ? 'none' : 'grayscale(100%) opacity(0.45)')};
  active { opacity: 0.8; }
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: 2 / 3;
  background-color: #2a2a2a;
  overflow: hidden;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardImgPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
  font-size: 10px;
`;

const CardFooter = styled.div`
  padding: 4px 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #1a1a1a;
`;

const CardIdLabel = styled.span`
  font-size: 10px;
  color: rgba(255,255,255,0.3);
`;

const RarityDot = styled.span<{ $rarity: CardRarity }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({ $rarity }) => RARITY_COLOR[$rarity]};
  flex-shrink: 0;
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 32px;
  font-size: 14px;
`;

const StatsBar = styled.div`
  background-color: rgba(255, 215, 0, 0.04);
  border: 1px solid rgba(255, 215, 0, 0.15);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StatsTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatMain = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
`;

const StatSub = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
`;

const ProgressTrack = styled.div`
  height: 4px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background-color: gold;
  border-radius: 3px;
`;

const RarityRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const RarityChip = styled.span<{ $color: string }>`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '';
    display: block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: ${({ $color }) => $color};
    flex-shrink: 0;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CardsListMobile: React.FC = () => {
  const isAuthenticated = useAppSelector(
    (state) => state.auth.isAuthenticated || state.auth.userLoggedState !== null
  );

  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ownedMap, setOwnedMap] = useState<Map<number, number>>(new Map());
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [search, setSearch] = useState('');
  const [expansionFilter, setExpansionFilter] = useState<number | 'all'>('all');
  const [onlyOwned, setOnlyOwned] = useState(isAuthenticated);

  const expansionOptions = useMemo(() => {
    const map = new Map<number, string>();
    cards.forEach((card) => card.expansions.forEach((exp) => map.set(exp.id, exp.name)));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [cards]);

  useEffect(() => {
    let isMounted = true;
    apiGetCards()
      .then((data) => {
        if (isMounted) {
          const list: Card[] = Array.isArray(data) ? data : (data as any).data ?? [];
          setCards(list);
        }
      })
      .finally(() => { if (isMounted) setIsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    apiGetMyOwnedCards()
      .then((data) => {
        const map = new Map<number, number>();
        const list = Array.isArray(data) ? data : (data as any).data ?? [];
        list.forEach((e: { card_id: number; quantity: number }) => map.set(e.card_id, e.quantity));
        setOwnedMap(map);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const collectionStats = useMemo(() => {
    if (!isAuthenticated || cards.length === 0) return null;
    const uniqueOwned = ownedMap.size;
    const pct = Math.round((uniqueOwned / cards.length) * 100);
    const byRarity = (['common', 'rare', 'epic', 'legendary'] as CardRarity[]).map((r) => {
      const total = cards.filter((c) => c.rarity === r).length;
      const owned = cards.filter((c) => c.rarity === r && ownedMap.has(c.id)).length;
      return { rarity: r, owned, total, color: RARITY_COLOR[r] };
    });
    return { uniqueOwned, pct, byRarity };
  }, [cards, ownedMap, isAuthenticated]);

  const filtered = useMemo(() => {
    return cards.filter((card) => {
      if (expansionFilter !== 'all' && !card.expansions.some((e) => e.id === expansionFilter)) return false;
      if (onlyOwned && !ownedMap.has(card.id)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          card.wrestlers.some((w) => w.name.toLowerCase().includes(q)) ||
          card.tags.some((t) => t.name.toLowerCase().includes(q)) ||
          card.ppvs.some((p) => (p.ppv_description?.name ?? '').toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [cards, expansionFilter, onlyOwned, search, ownedMap]);

  return (
    <>
      <Container>
        <Title>Lista Carte</Title>

        <FiltersRow>
          <SearchInput
            type="text"
            placeholder="Cerca per wrestler, tag, PPV..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {isAuthenticated && (
            <ToggleOwnedBtn
              $active={onlyOwned}
              onClick={() => setOnlyOwned((v) => !v)}
            >
              Solo mie
            </ToggleOwnedBtn>
          )}
        </FiltersRow>

        {expansionOptions.length > 0 && (
          <ExpansionSelect
            value={expansionFilter}
            onChange={(e) => setExpansionFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value="all">Tutte le espansioni</option>
            {expansionOptions.map((exp) => (
              <option key={exp.id} value={exp.id}>{exp.name}</option>
            ))}
          </ExpansionSelect>
        )}

        {collectionStats && (
          <StatsBar>
            <StatsTopRow>
              <StatMain>{collectionStats.uniqueOwned} / {cards.length} carte</StatMain>
              <StatSub>{collectionStats.pct}%</StatSub>
            </StatsTopRow>
            <ProgressTrack>
              <ProgressFill $pct={collectionStats.pct} />
            </ProgressTrack>
            <RarityRow>
              {collectionStats.byRarity.map(({ rarity, owned, total, color }) => (
                <RarityChip key={rarity} $color={color}>{owned}/{total}</RarityChip>
              ))}
            </RarityRow>
          </StatsBar>
        )}

        <CountText>{filtered.length} carte</CountText>

        {isLoading && <StatusText>Caricamento...</StatusText>}
        {!isLoading && cards.length === 0 && <StatusText>Nessuna carta disponibile.</StatusText>}

        <Grid>
          {filtered.map((card) => {
            const src = toImgSrc(card.fronte_path);
            const owned = ownedMap.has(card.id);
            return (
              <CardItem key={card.id} $owned={owned} onClick={() => setSelectedCard(card)}>
                <CardImgWrapper>
                  {src
                    ? <CardImg src={src} alt={`Carta #${card.id}`} />
                    : <CardImgPlaceholder>#{card.id}</CardImgPlaceholder>
                  }
                </CardImgWrapper>
                <CardFooter>
                  <CardIdLabel>#{card.id}</CardIdLabel>
                  <RarityDot $rarity={card.rarity} />
                </CardFooter>
              </CardItem>
            );
          })}
        </Grid>
      </Container>

      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          ownedQuantity={ownedMap.get(selectedCard.id) ?? 0}
          isAuthenticated={isAuthenticated}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </>
  );
};

export default CardsListMobile;
